import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Tag,
  TagLabel,
  Text,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { MdArrowForward } from "react-icons/md";
import { useState } from "react";
import { toast } from "react-toastify";
import { fetchController } from "../../../utils/FetchController/fetchController";
import { getHostDomain } from "../../../utils/utils";
import { HeroSmartStart, IconChat, IconVoice } from "../Illustrations";

function ChannelCard({ channel, icon, accent, gradient, title, subtitle, tags, onPick }) {
  return (
    <Box
      role="group"
      cursor="pointer"
      onClick={() => onPick(channel)}
      borderRadius="xl"
      overflow="hidden"
      position="relative"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      transition="all 0.2s ease"
      _hover={{
        transform: "translateY(-4px)",
        shadow: "xl",
        borderColor: `${accent}.300`,
      }}
    >
      {/* Gradient accent header */}
      <Box bgGradient={gradient} h="6px" w="100%" />

      <Box p={5}>
        <Flex align="center" justify="space-between" mb={3}>
          {/* Icon disc */}
          <Flex
            w="56px"
            h="56px"
            borderRadius="lg"
            bgGradient={gradient}
            align="center"
            justify="center"
            shadow="md"
            transition="transform 0.2s ease"
            _groupHover={{ transform: "scale(1.05)" }}
          >
            {icon}
          </Flex>
          {/* Click affordance */}
          <Flex
            w="36px"
            h="36px"
            borderRadius="full"
            bg={`${accent}.50`}
            color={`${accent}.500`}
            align="center"
            justify="center"
            transition="all 0.2s ease"
            _groupHover={{ bg: `${accent}.500`, color: "white" }}
          >
            <Icon as={MdArrowForward} boxSize={5} />
          </Flex>
        </Flex>

        <Heading size="md" mb={1} color="gray.800">
          {title}
        </Heading>
        <Text fontSize="sm" color="gray.600" mb={4}>
          {subtitle}
        </Text>

        {tags?.length > 0 && (
          <HStack spacing={1} flexWrap="wrap">
            {tags.map((t) => (
              <Badge
                key={t}
                colorScheme={accent}
                variant="subtle"
                fontSize="0.65rem"
                textTransform="none"
                fontWeight="500"
                px={2}
                py="2px"
                borderRadius="full"
              >
                {t}
              </Badge>
            ))}
          </HStack>
        )}
      </Box>
    </Box>
  );
}

const EXAMPLES = [
  {
    short: "📞 Dental booking",
    full: "I want a voice answering machine that books appointments for my dental practice. We do cleanings, fillings, and orthodontics, open Monday to Friday, 9 to 5.",
  },
  {
    short: "💼 SaaS demo qualifier",
    full: "I need a chat agent on my website to qualify inbound demo requests for our analytics SaaS, capture name + work email + company size, and book a 30-minute demo with the right account executive.",
  },
  {
    short: "🍽️ Restaurant reservations",
    full: "Build a phone agent that takes restaurant reservations for our farm-to-table bistro, open Tuesday to Sunday from 5 PM to 10:30 PM. Confirm via SMS after booking.",
  },
  {
    short: "🏡 Real estate leads",
    full: "I want a chat agent for my real estate website that qualifies buyers (budget, area, timeline) and books a showing on my Google Calendar. Push every lead to HubSpot.",
  },
  {
    short: "🛒 E-commerce returns",
    full: "Create a chat agent that handles return and refund requests for my Shopify store. Look up the order, confirm eligibility, and open a Freshdesk ticket once the user confirms.",
  },
  {
    short: "🛠️ Service intake",
    full: "Build a voice agent that takes service-call requests for my plumbing business, captures address + issue + urgency, and texts the on-call plumber if it's an emergency after hours.",
  },
];

export default function Step0SmartStart({ onConfigParsed, onChannelPicked }) {
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (description.trim().length < 10) {
      toast.error("Tell us a bit more about what you want to build (10+ characters).");
      return;
    }
    setLoading(true);
    try {
      const res = await fetchController(
        getHostDomain() + "/api/v1/wizard/parse-intent",
        "POST",
        { description: description.trim() }
      );
      const config = res?.data;
      if (!config) throw new Error("No config returned");
      onConfigParsed(config);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.message ||
          "Couldn't parse that — try picking a template instead."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack spacing={6} align="stretch" maxW="720px" mx="auto" w="full">
      <HeroSmartStart size={200} />
      <Box textAlign="center">
        <Heading size="lg" mb={2}>What do you want to build?</Heading>
        <Text color="gray.600">
          Describe your agent in your own words. We'll wire up the right
          fields, tools, and prompt for you.
        </Text>
      </Box>

      <Textarea
        placeholder='e.g. "I want a voice answering machine that books appointments for my dental practice — we do cleanings and orthodontics, open Mon–Fri 9 to 5."'
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={5}
        size="lg"
        bg="white"
      />

      <Box>
        <Text fontSize="xs" color="gray.500" mb={2} textAlign="center">
          ✨ Try an example to get going
        </Text>
        <Wrap justify="center" spacing={2}>
          {EXAMPLES.map((ex) => (
            <WrapItem key={ex.short}>
              <Tag
                size="md"
                borderRadius="full"
                variant="subtle"
                colorScheme="blue"
                cursor="pointer"
                onClick={() => setDescription(ex.full)}
                _hover={{ bg: "blue.100" }}
              >
                <TagLabel>{ex.short}</TagLabel>
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      </Box>

      <Button
        colorScheme="blue"
        size="lg"
        onClick={submit}
        isDisabled={loading || description.trim().length < 10}
      >
        {loading ? <Spinner size="sm" mr={2} /> : "✨ "}
        {loading ? "Building..." : "Build it"}
      </Button>

      <HStack>
        <Divider />
        <Text fontSize="sm" color="gray.500" whiteSpace="nowrap" px={2}>
          or pick a starting point
        </Text>
        <Divider />
      </HStack>

      <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
        <ChannelCard
          channel="chat"
          icon={<IconChat size={32} color="#fff" />}
          accent="blue"
          gradient="linear(135deg, #3B82F6 0%, #2563EB 100%)"
          title="Chat"
          subtitle="Embed on your website or in-app"
          tags={["Webpages", "WhatsApp", "Messenger"]}
          onPick={onChannelPicked}
        />
        <ChannelCard
          channel="voice"
          icon={<IconVoice size={32} color="#fff" />}
          accent="purple"
          gradient="linear(135deg, #8B5CF6 0%, #6D28D9 100%)"
          title="Voice"
          subtitle="Answer phone calls 24/7"
          tags={["Twilio", "Plivo", "Telnyx", "SIP"]}
          onPick={onChannelPicked}
        />
      </SimpleGrid>
    </VStack>
  );
}
